import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../i18n";
import { useSession } from "../auth/session";
import { setPreferredLanguage, toStorableLanguage } from "../profile/profileApi";
import { noteLanguageStated } from "../profile/preferredLanguage";
import {
  ApplicationNotEntitledError,
  SessionExpiredError,
  TenantSuspendedError,
} from "../lib/api";

// FI/EN switcher shown on the landing screen and in the app header.
// The selection persists via i18next's localStorage detector (see src/i18n).
//
// For a SIGNED-IN reader it is also the self-service mail-language setting (KM-E11 follow-up): the same
// choice is written to their user row (`PUT /api/profile/language`), and the questionnaire mail paths
// resolve each recipient's language from exactly that value. This switcher IS the surface rather than a
// separate profile field, because a second control asking the same question — "what language do you
// read?" — is a control two answers can disagree about, and the disagreement would only ever show up in
// somebody's inbox, days later, where nobody can correct it.
//
// The write never blocks the UI change — switching the language is a local, instant thing — but it is NOT
// silent when it fails. The backend orders the two writes identity-provider-first and stores nothing when
// Keycloak refuses (502), so a failure means the mail language is still the OLD one; saying nothing would
// leave the person believing they had changed something they had not, and the only place the truth would
// surface is an inbox, days later. So a failure renders next to the control, and the mail language the
// person still has is the one that reaches them. The exceptions are the refusals that are not about this
// write at all and whose own screen is already on the way or already drawn — a 401 (the shared apiFetch
// hook is redirecting to login) and the two tenant-level 403s; see the catch below.
//
// Pre-session (the login/email-link screens render this too) there is no row to write to, so it stays a
// purely local choice until sign-in — where PreferredLanguageSync seeds the row from it.
export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const { session } = useSession();
  const [failed, setFailed] = useState(false);
  const current = i18n.language.split("-")[0];

  const onChange = useCallback(
    (value: string) => {
      // THIS IS THE STATEMENT, and it is recorded before anything else so that nothing which outlives it
      // can undo it. The sign-in reconciliation (profile/PreferredLanguageSync.tsx) settles the row and
      // the screen against each other on every signed-in load, and it can still be waiting for the tenant
      // when this fires — it then reads the row as it was BEFORE the write below, and answers with the
      // language this switch just left. A person's own choice is the later answer either way, so the
      // reconciliation stands aside for it from here on (profile/preferredLanguage.ts).
      noteLanguageStated();
      void i18n.changeLanguage(value);
      if (!session) return;
      const storable = toStorableLanguage(value);
      if (!storable) return;
      setFailed(false);
      void setPreferredLanguage(storable)
        .catch((error: unknown) => {
          // Three refusals say nothing worth putting beside the control. A 401 is already redirecting
          // to login. The two TENANT-level 403s (src/auth/tenantAccess.tsx) store nothing at all, and
          // the screen this switcher is drawn on is the terminal screen for that very refusal — it
          // already explains, in the language just selected, why the whole organisation is out. A
          // second `role="alert"` saying "language could not be saved" beside it would read as an
          // unrelated fault of the reader's own, and the switcher is the ONE control that still works
          // on those screens, so this is not a corner: it is what the reader does there.
          //   • the freeze (decision f3ca7f9e): TenantSuspendedError
          //   • the unbought application (ADR-0014 §7.1 step 3): ApplicationNotEntitledError
          setFailed(
            !(error instanceof SessionExpiredError) &&
              !(error instanceof TenantSuspendedError) &&
              !(error instanceof ApplicationNotEntitledError),
          );
        });
    },
    [i18n, session],
  );

  return (
    <div className="lang-switcher">
      <label htmlFor="lang-select">{t("app.languageLabel")}</label>
      <select
        id="lang-select"
        value={SUPPORTED_LANGUAGES.includes(current as never) ? current : "fi"}
        onChange={(e) => onChange(e.target.value)}
      >
        {SUPPORTED_LANGUAGES.map((lng) => (
          <option key={lng} value={lng}>
            {t(`app.language.${lng}`)}
          </option>
        ))}
      </select>
      {failed && (
        <p className="lang-switcher__error" role="alert" data-testid="lang-save-failed">
          {t("app.languageSaveFailed")}
        </p>
      )}
    </div>
  );
}
